import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { Delta } from "./Delta";
import { Sparkline } from "./Sparkline";

/**
 * Instrument strip: one shared surface, cells divided by hairlines.
 * Children are KpiCard elements. `cols` = desktop column count (2–6).
 */
const COLS = { 2: "md:grid-cols-2", 3: "md:grid-cols-3", 4: "md:grid-cols-4", 5: "md:grid-cols-5", 6: "md:grid-cols-6" };

export const KpiStrip = ({ children, cols = 4, className, testId }) => (
  <div className={cn("panel grid grid-cols-2 overflow-hidden [&>*]:border-border [&>*:nth-child(n+3)]:border-t [&>*:nth-child(even)]:border-l", "md:[&>*]:border-t-0 md:[&>*]:border-l md:[&>*:first-child]:border-l-0", COLS[cols] || COLS[4], className)} data-testid={testId}>
    {children}
  </div>
);

/**
 * A single instrument cell: mono label → large quiet numeral → context line.
 * tone: default | alert (value in rose) | primary
 */
export const KpiCard = ({ label, value, unit, sub, delta, deltaSuffix, deltaInvert, loading, className, tone = "default", spark, sparkColor, standalone = false, testId, children }) => {
  return (
    <div className={cn("group relative flex min-h-[108px] flex-col justify-between bg-card px-5 py-4", standalone && "panel", className)} data-testid={testId}>
      <div className="label">{label}</div>
      {loading ? (
        <Skeleton className="mt-3 h-8 w-24" />
      ) : (
        <div className="mt-3 flex items-baseline gap-1.5">
          <span className={cn("numeral text-[28px] sm:text-[32px]", tone === "alert" && "text-rose-600 dark:text-rose-300", tone === "primary" && "text-primary")} data-testid={testId ? `${testId}-value` : undefined}>
            {value}
          </span>
          {unit ? <span className="mono text-[12px] text-muted-foreground">{unit}</span> : null}
        </div>
      )}
      <div className="mt-2.5 flex min-h-[16px] items-center gap-2 text-[12px] text-muted-foreground">
        {!loading && delta !== undefined ? <Delta value={delta} suffix={deltaSuffix} invert={deltaInvert} /> : null}
        {sub ? <span className="truncate">{sub}</span> : null}
      </div>
      {spark && spark.length > 2 && !loading ? (
        <div className="pointer-events-none absolute bottom-4 right-5 opacity-70">
          <Sparkline values={spark} width={72} height={24} color={sparkColor} fill={false} />
        </div>
      ) : null}
      {children}
    </div>
  );
};
